import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminGetMe,
  adminLogout,
  adminGetStats,
  adminListUsers,
  adminDeleteUser,
  adminListPosts,
  adminDeletePost,
  adminListGalaxies,
  adminDeleteGalaxy,
} from '../lib/adminApi';
import { toast } from 'sonner';

const TABS = ['Overview', 'Users', 'Posts', 'Galaxies'];

export default function AdminPanel() {
  const nav = useNavigate();
  const [tab, setTab] = useState('Overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [galaxies, setGalaxies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);
  const [pending, setPending] = useState({});

  useEffect(() => {
    let mounted = true;
    adminGetMe()
      .then(() => { if (mounted) load(); })
      .catch(() => { if (mounted) nav('/admin/login', { replace: true }); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Promise.allSettled instead of Promise.all: one failing endpoint (e.g.
  // a transient galaxy-count error) must not blank out tabs whose data
  // loaded fine -- each panel shows what it has and reports its own error.
  const load = async () => {
    setLoading(true);
    setLoadErrors([]);
    const [s, u, p, g] = await Promise.allSettled([
      adminGetStats(),
      adminListUsers(),
      adminListPosts(),
      adminListGalaxies(),
    ]);

    const errors = [];
    if (s.status === 'fulfilled') setStats(s.value); else errors.push(`Stats: ${s.reason.message}`);
    if (u.status === 'fulfilled') setUsers(u.value); else errors.push(`Users: ${u.reason.message}`);
    if (p.status === 'fulfilled') setPosts(p.value); else errors.push(`Posts: ${p.reason.message}`);
    if (g.status === 'fulfilled') setGalaxies(g.value); else errors.push(`Galaxies: ${g.reason.message}`);

    setLoadErrors(errors);
    errors.forEach((msg) => toast(msg));
    if (errors.some((msg) => /session expired/i.test(msg))) {
      nav('/admin/login', { replace: true });
    }
    setLoading(false);
  };

  const logout = async () => {
    await adminLogout().catch(() => {});
    nav('/admin/login', { replace: true });
  };

  const withPending = async (key, action) => {
    if (pending[key]) return;
    setPending((p) => ({ ...p, [key]: true }));
    try {
      await action();
    } finally {
      setPending((p) => ({ ...p, [key]: false }));
    }
  };

  const removeUser = (id) => withPending(`user-${id}`, async () => {
    if (!window.confirm('Delete this user and all their data? This cannot be undone.')) return;
    try {
      await adminDeleteUser(id);
      setUsers((u) => u.filter((x) => x.id !== id));
      toast('User deleted');
    } catch (err) {
      toast(err.message || 'Delete failed');
    }
  });

  const removePost = (id) => withPending(`post-${id}`, async () => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await adminDeletePost(id);
      setPosts((p) => p.filter((x) => x.id !== id));
      toast('Post deleted');
    } catch (err) {
      toast(err.message || 'Delete failed');
    }
  });

  const removeGalaxy = (id) => withPending(`galaxy-${id}`, async () => {
    if (!window.confirm('Delete this galaxy?')) return;
    try {
      await adminDeleteGalaxy(id);
      setGalaxies((g) => g.filter((x) => x.id !== id));
      toast('Galaxy deleted');
    } catch (err) {
      toast(err.message || 'Delete failed');
    }
  });

  return (
    <div className="min-h-screen w-full" style={{ background: '#0d0d12', color: '#e5e5ea' }}>
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold">Orbit Admin</h1>
          <button onClick={logout} className="press text-sm px-4 h-10 rounded-lg" style={{ background: '#3b3f51' }}>
            Log out
          </button>
        </div>

        {loadErrors.length > 0 && (
          <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: '#3a1f1f', color: '#ff9d9d', border: '1px solid #5a2a2a' }}>
            {loadErrors.map((msg) => <div key={msg}>{msg}</div>)}
          </div>
        )}

        <div className="flex gap-4 mb-6 border-b" style={{ borderColor: '#2a2b35' }}>
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="press h-10 px-2 font-semibold"
              style={{ color: tab === t ? '#fff' : '#6f6f7a', borderBottom: tab === t ? '2px solid #7c6fd6' : 'none' }}
            >
              {t}
            </button>
          ))}
        </div>

        {loading && <p style={{ color: '#8a8a94' }}>Loading…</p>}

        {!loading && tab === 'Overview' && stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(stats).map(([key, val]) => (
              <div key={key} className="p-4 rounded-lg" style={{ background: '#17181f', border: '1px solid #2a2b35' }}>
                <div className="text-2xl font-bold">{val}</div>
                <div className="text-sm" style={{ color: '#8a8a94' }}>{key}</div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === 'Users' && (
          <div className="space-y-2">
            {users.length === 0 && <p style={{ color: '#8a8a94' }}>No users.</p>}
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#17181f', border: '1px solid #2a2b35' }}>
                <div>
                  <div className="font-semibold">{u.display_name} <span style={{ color: '#8a8a94' }}>@{u.username}</span></div>
                  <div className="text-xs" style={{ color: '#6f6f7a' }}>{u.id}</div>
                </div>
                <button onClick={() => removeUser(u.id)} disabled={!!pending[`user-${u.id}`]} className="press text-sm px-3 h-10 rounded-lg" style={{ background: '#5a2a2a', color: '#ff9d9d', opacity: pending[`user-${u.id}`] ? 0.5 : 1 }}>
                  {pending[`user-${u.id}`] ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === 'Posts' && (
          <div className="space-y-2">
            {posts.length === 0 && <p style={{ color: '#8a8a94' }}>No posts.</p>}
            {posts.map((p) => (
              <div key={p.id} className="flex items-start justify-between p-3 rounded-lg" style={{ background: '#17181f', border: '1px solid #2a2b35' }}>
                <div className="flex-1 min-w-0 mr-3">
                  <div className="text-sm font-semibold">@{p.author?.username ?? 'unknown'} <span style={{ color: '#6f6f7a' }}>· {p.audience}</span></div>
                  <div className="text-sm truncate" style={{ color: '#c5c5cc' }}>{p.body}</div>
                </div>
                <button onClick={() => removePost(p.id)} disabled={!!pending[`post-${p.id}`]} className="press text-sm px-3 h-10 rounded-lg shrink-0" style={{ background: '#5a2a2a', color: '#ff9d9d', opacity: pending[`post-${p.id}`] ? 0.5 : 1 }}>
                  {pending[`post-${p.id}`] ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === 'Galaxies' && (
          <div className="space-y-2">
            {galaxies.length === 0 && <p style={{ color: '#8a8a94' }}>No galaxies.</p>}
            {galaxies.map((g) => (
              <div key={g.id} className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#17181f', border: '1px solid #2a2b35' }}>
                <div>
                  <div className="font-semibold">{g.name}</div>
                  <div className="text-xs" style={{ color: '#8a8a94' }}>owner: @{g.owner?.username ?? 'unknown'}</div>
                </div>
                <button onClick={() => removeGalaxy(g.id)} disabled={!!pending[`galaxy-${g.id}`]} className="press text-sm px-3 h-10 rounded-lg" style={{ background: '#5a2a2a', color: '#ff9d9d', opacity: pending[`galaxy-${g.id}`] ? 0.5 : 1 }}>
                  {pending[`galaxy-${g.id}`] ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
